import { AbstractControl, ValidationErrors } from '@angular/forms';
import { Globals } from '../../../globals';

export class CardFormHelper {
	private static readonly luhnArr = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];

	// Implementación más eficiente del alg. de Luhn 
	private static luhnChk(ccNum: string): boolean {
		let len = ccNum.length;
		let bit = 1;
		let sum = 0;
		let val: number;

		while (len) {
			val = parseInt(ccNum.charAt(--len), 10);
			sum += (bit ^= 1) ? CardFormHelper.luhnArr[val] : val;
		}

		return !!sum && sum % 10 === 0;
	}

	private static maxLengths: { [key: string]: number } = {
		number: 19,     // "XXXX XXXX XXXX XXXX"
		expiry: 7,      // "MM / YY"
		cvc: 3,         // "NNN"
		name: 26        // "Nombre y apellido"
	};

	static isLastCharacterForInput(controlName: string, value: string): boolean {
		return value.length === CardFormHelper.maxLengths[controlName];
	}

	static formatPanGroups(digits: string): string {
		return digits.replace(/(.{4})/g, '$1 ').trim();
	}

	static caretPosFromDigitsIndex(formatted: string, digitsIndex: number): number {
		if (digitsIndex <= 0) return 0;

		let seen = 0;
		for (let i = 0; i < formatted.length; i++) {
			const code = formatted.charCodeAt(i);
			if (code >= 48 && code <= 57) {
				seen++;
				if (seen === digitsIndex) {
					return i + 1;
				}
			}
		}

		return formatted.length;
	}

	static validateCardNumber(control: AbstractControl): ValidationErrors | null {
		const rawValue = control.value ?? '';
		const value = String(rawValue).replace(/\s/g, '');

		const isValidLength = /^[0-9]{16}$/.test(value); // 15: AE, 16: visa, mastercard, etc
		const isValidLuhn = Globals.isDevEnv ? true : CardFormHelper.luhnChk(value);

		return isValidLength && isValidLuhn ? null : { invalidCardNumber: true };
	}

	static validateCardType(control: AbstractControl): ValidationErrors | null {
		const rawValue = control.value ?? '';
		const value = String(rawValue).replace(/\s/g, '');

		// Visa (starts with 4)
		const isVisa = value.startsWith('4');

		const first2 = parseInt(value.slice(0, 2), 10);
		const first4 = parseInt(value.slice(0, 4), 10);

		// Mastercard (starts with 51-55 or 2221-2720)
		const isMasterCard = (first2 >= 51 && first2 <= 55) || (first4 >= 2221 && first4 <= 2720);

		const isValidCardType = isVisa || isMasterCard;
		return isValidCardType ? null : { invalidCardType: true };
	}

	static validateExpiryDate(control: AbstractControl): ValidationErrors | null {
		const rawValue = control.value ?? '';
		const value = String(rawValue);

		const [month, year] = value.split(' / ').map(Number);

		if ((!month && month !== 0) || !year) return { invalidDate: true };

		const currentYear = new Date().getFullYear() % 100; // Last 2 digits
		const currentMonth = new Date().getMonth() + 1; // JavaScript months are 0-based

		if (month < 1 || month > 12) {
			return { invalidMonth: true };
		}
		if (year < currentYear) {
			return { invalidYear: true };
		}
		if (year === currentYear && month < currentMonth) {
			return { expired: true };
		}

		return null;
	}

	static validateCardHolderName(control: AbstractControl): ValidationErrors | null {
		const rawValue = control.value ?? '';
		const value = String(rawValue);
		const regex = /^[a-zA-Z\u00C0-\u017F\s'`~.\-]{1,26}$/;
		return regex.test(value) ? null : { invalidCardHolderName: true };
	}

	static iOSNameInputCorrection(event: InputEvent | any) {
		const input = event.target as HTMLInputElement;
		const keyupEvent = new KeyboardEvent('keyup', { bubbles: true, cancelable: true });
		input.dispatchEvent(keyupEvent);
	}
}
